/**
 * Per-area CSS class stamping on dockview chrome (架构方案 §5.7). dockview's
 * serialized tree does not carry our column-level classes, so they are (re)applied
 * imperatively after every layout change / restore via {@link applyAreaViewClasses}.
 */
import type { LayoutArea } from '@hibro/shared';
import type { DockviewApi } from 'dockview-react';

import { AREA_SPECS } from './area-specs';

/** Default `.dv-view` / `.dv-groupview` class for an area. */
function areaViewClass(area: LayoutArea): string {
  return AREA_SPECS[area].viewClass ?? `app-area-${area}-view`;
}

/** Leading sash class when {@link AreaSpec.leadingSash} is set. */
function areaLeadingSashClass(area: LayoutArea): string {
  return `app-area-${area}-leading-sash`;
}

/** Trailing sash class when {@link AreaSpec.trailingSash} is set. */
function areaTrailingSashClass(area: LayoutArea): string {
  return `app-area-${area}-trailing-sash`;
}

/** Every class this module may stamp — cleared before re-apply. */
function allAreaChromeClasses(): string[] {
  const areas = Object.keys(AREA_SPECS) as LayoutArea[];
  const classes = new Set<string>();
  for (const area of areas) {
    classes.add(areaViewClass(area));
    const branch = AREA_SPECS[area].branchViewClass;
    if (branch) {
      classes.add(branch);
    }
    if (AREA_SPECS[area].leadingSash) {
      classes.add(areaLeadingSashClass(area));
    }
    if (AREA_SPECS[area].trailingSash) {
      classes.add(areaTrailingSashClass(area));
    }
  }
  return [...classes];
}

function getAreaChromeEl(
  panel: NonNullable<ReturnType<DockviewApi['getPanel']>>,
): HTMLElement | undefined {
  const contentEl = document.getElementById(
    panel.group.model.contentContainerId,
  );
  const chromeEl =
    contentEl?.closest('.dv-view') ?? contentEl?.closest('.dv-groupview');
  return chromeEl instanceof HTMLElement ? chromeEl : undefined;
}

/** Outer `.dv-view` wrapping a nested `.dv-branch-node` (editor/panel column). */
function findBranchParentView(leafView: HTMLElement): HTMLElement | undefined {
  const branchNode = leafView.closest('.dv-branch-node');
  const outerView = branchNode?.parentElement;
  if (
    outerView instanceof HTMLElement &&
    outerView.classList.contains('dv-view')
  ) {
    return outerView;
  }
  return undefined;
}

function horizontalSplitParts(viewEl: HTMLElement):
  | {
      views: Element[];
      sashes: Element[];
      viewIndex: number;
    }
  | undefined {
  const splitRoot = viewEl.closest('.dv-split-view-container.dv-horizontal');
  if (!(splitRoot instanceof HTMLElement)) {
    return undefined;
  }
  const views = [
    ...splitRoot.querySelectorAll(':scope > .dv-view-container > .dv-view'),
  ];
  const viewIndex = views.indexOf(viewEl);
  if (viewIndex < 0) {
    return undefined;
  }
  const sashes = [
    ...splitRoot.querySelectorAll(':scope > .dv-sash-container > .dv-sash'),
  ];
  return { views, sashes, viewIndex };
}

/** `.dv-sash` immediately before `viewEl` in the same horizontal splitview. */
function findLeadingSash(viewEl: HTMLElement): HTMLElement | undefined {
  const parts = horizontalSplitParts(viewEl);
  if (!parts || parts.viewIndex <= 0) {
    return undefined;
  }
  const sashEl = parts.sashes[parts.viewIndex - 1];
  return sashEl instanceof HTMLElement ? sashEl : undefined;
}

/** `.dv-sash` immediately after `viewEl` in the same horizontal splitview. */
function findTrailingSash(viewEl: HTMLElement): HTMLElement | undefined {
  const parts = horizontalSplitParts(viewEl);
  if (!parts || parts.viewIndex >= parts.views.length - 1) {
    return undefined;
  }
  const sashEl = parts.sashes[parts.viewIndex];
  return sashEl instanceof HTMLElement ? sashEl : undefined;
}

/**
 * Stamp per-area classes on dockview chrome (`.dv-view` / `.dv-groupview`) and
 * optional leading / trailing sashes. Uses `contentContainerId` —
 * `model.element` throws in this fork. Re-run after layout changes
 * (drag / restore).
 */
export function applyAreaViewClasses(api: DockviewApi): void {
  const classes = allAreaChromeClasses();
  const selector = classes
    .map((c) => `.app-dockview .${CSS.escape(c)}`)
    .join(', ');
  if (selector) {
    document.querySelectorAll(selector).forEach((el) => {
      el.classList.remove(...classes);
    });
  }

  for (const area of Object.keys(AREA_SPECS) as LayoutArea[]) {
    const spec = AREA_SPECS[area];
    const panel = api.getPanel(area);
    if (!panel) {
      continue;
    }

    const chromeEl = getAreaChromeEl(panel);
    if (!chromeEl) {
      continue;
    }

    chromeEl.classList.add(areaViewClass(area));

    if (spec.branchViewClass && chromeEl.classList.contains('dv-view')) {
      findBranchParentView(chromeEl)?.classList.add(spec.branchViewClass);
    }

    if (spec.leadingSash && chromeEl.classList.contains('dv-view')) {
      findLeadingSash(chromeEl)?.classList.add(areaLeadingSashClass(area));
    }
    if (spec.trailingSash && chromeEl.classList.contains('dv-view')) {
      findTrailingSash(chromeEl)?.classList.add(areaTrailingSashClass(area));
    }
  }
}

/**
 * The dockview chrome (`.dv-view` / `.dv-groupview`) wrapping an area's group,
 * tab strip included. Identity comes from the area's anchor panel through the
 * API, not from the classes stamped above, so it holds between re-stamps.
 */
export function findAreaChromeEl(
  api: DockviewApi,
  area: LayoutArea,
): HTMLElement | undefined {
  const panel = api.getPanel(area);
  return panel ? getAreaChromeEl(panel) : undefined;
}

/**
 * The sash that resizes a leftmost grid column (the sash *after* its `.dv-view`).
 * Re-queried at event time because dockview rebuilds sash DOM on layout changes.
 */
export function findAreaTrailingSash(
  api: DockviewApi,
  area: LayoutArea,
): HTMLElement | undefined {
  const chromeEl = findAreaChromeEl(api, area);
  if (!chromeEl?.classList.contains('dv-view')) {
    return undefined;
  }
  return findTrailingSash(chromeEl);
}
